import { useEffect, useState } from 'react'
import { useAppDispatch } from '@/store/store-hooks'
import { Button } from '@/components/button/Button'
import { CircularProgressContinuousSized } from '@/components/loaders/CircularProgressContinuous'
import { Modal, ModalContent } from '@/components/modal'
import { FilePdf } from '@/assets/icons'
import { ApiError } from '@/util/errors/api-error'
import { isoDateStringToMonthDayYear } from '@/util/time'
import { AnalyticsEvent } from '@/analytics/schema/events.schema'
import { trackMedSummaryEvent } from '@/analytics/utils/medsummary-analytics'
import { openGlobalToast } from '@/store/slices/global-toast.slice'
import { GlobalToastType, kToastDurationShortMs } from '@/constants/constants-ui'
import { getRemovalImpact } from '../../fetch/get-removal-impact'
import { deleteAssetFile } from '../../fetch/delete-asset-file'
import { MedSummaryAssetActions } from '../../store/slice'
import { RemovalImpactResponse } from '../../store/schemas'

type RemoveFileDialogProps = {
  open: boolean
  assetId: string
  matterId: string
  userFileId: string
  displayName: string
  onClose: () => void
}

const kAmountFormatter = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' })

/**
 * Remove-file confirmation: fetches the removal impact on open (entry count, total amount,
 * affected date range), then synchronously removes the file's entries from the billing summary.
 */
export default function RemoveFileDialog({ open, assetId, matterId, userFileId, displayName, onClose }: RemoveFileDialogProps) {
  const dispatch = useAppDispatch()

  const [impact, setImpact] = useState<RemovalImpactResponse | null>(null)
  const [impactError, setImpactError] = useState(false)
  const [removing, setRemoving] = useState(false)
  const [removeError, setRemoveError] = useState<string | null>(null)

  useEffect(() => {
    if (!open) return
    setImpact(null)
    setImpactError(false)
    setRemoveError(null)

    getRemovalImpact(assetId, userFileId)
      .then(setImpact)
      .catch((error) => {
        console.error('Error fetching removal impact:', error)
        setImpactError(true)
      })
  }, [open, assetId, userFileId])

  const handleRemove = async () => {
    if (removing) return
    setRemoving(true)
    setRemoveError(null)
    try {
      const result = await deleteAssetFile(assetId, userFileId)
      trackMedSummaryEvent(AnalyticsEvent.MedSummaryAssetFileRemoved, { medSummaryAssetId: assetId, medSummaryEntriesRemoved: result.entries_removed })
      // Server truth: refresh the overview, entries, and roster after the removal
      dispatch(MedSummaryAssetActions.fetchAssetForMatter({ matterId }))
      dispatch(MedSummaryAssetActions.refreshAssetData({ assetId }))
      // Removal is immediate (no regeneration wait), so confirm it with a toast.
      dispatch(
        openGlobalToast({ type: GlobalToastType.SUCCESS, message: `Removed ${displayName} from the billing summary`, durationMs: kToastDurationShortMs })
      )
      onClose()
    } catch (error) {
      if (error instanceof ApiError && error.status === 409) {
        setRemoveError('The billing summary is being updated. Try again when the current update finishes.')
      } else {
        setRemoveError('Could not remove the file. Please try again.')
      }
    } finally {
      setRemoving(false)
    }
  }

  if (!open) return null

  const impactSentence = (() => {
    if (impactError) return 'The impact summary could not be loaded. Removing this file will delete its entries from the billing summary.'
    if (!impact) return null
    const entriesPhrase = `${impact.entries_count} ${impact.entries_count === 1 ? 'entry' : 'entries'} totaling ${kAmountFormatter.format(impact.total_amount)}`
    if (impact.earliest_service_date && impact.latest_service_date) {
      return `Removing this file will delete ${entriesPhrase} from ${isoDateStringToMonthDayYear(
        impact.earliest_service_date
      )} – ${isoDateStringToMonthDayYear(impact.latest_service_date)}.`
    }
    return `Removing this file will delete ${entriesPhrase}.`
  })()

  const footer = (
    <div className="flex items-center justify-end gap-3">
      {/* Hand-rolled to match the confirm-dialog pattern (e.g. DeleteMatterDialog): #121212
          (brand-neutral-950) border + label vs the shared Button outlined-neutral's muted DS neutral. */}
      <button
        type="button"
        onClick={onClose}
        disabled={removing}
        className="min-w-20 px-4 py-2 text-sm font-semibold text-brand-neutral-950 rounded-md ring-1 ring-inset ring-brand-neutral-950 focus:outline-none hocus:bg-brand-neutral-100 disabled:opacity-50 flex items-center justify-center"
      >
        Cancel
      </button>
      <Button variant="filled" color="destructive" onClick={handleRemove} disabled={removing || impactSentence === null}>
        {removing ? (
          <span className="inline-flex items-center gap-2">
            <CircularProgressContinuousSized size={14} thickness={4} />
            Remove file
          </span>
        ) : (
          'Remove file'
        )}
      </Button>
    </div>
  )

  return (
    <Modal
      open={open}
      onOpenChange={(nextOpen) => {
        if (!nextOpen && !removing) onClose()
      }}
    >
      <ModalContent title="Remove file from billing summary" width="md" height="max-md" footer={footer}>
        <div className="flex flex-col gap-2">
          {impactSentence === null ? (
            <div className="flex items-center gap-2 text-sm text-brand-neutral-600">
              <CircularProgressContinuousSized size={14} thickness={4} />
              Calculating impact…
            </div>
          ) : (
            <p className="text-base leading-6 tracking-[-0.32px] text-brand-neutral-950">{impactSentence}</p>
          )}

          <div className="flex items-center gap-2.5">
            <FilePdf className="w-6 h-6 text-brand-neutral-950 shrink-0" />
            <span className="text-sm font-medium tracking-[-0.15px] text-brand-neutral-950 truncate" title={displayName}>
              {displayName}
            </span>
          </div>

          {removeError && <p className="text-sm text-red-700">{removeError}</p>}
        </div>
      </ModalContent>
    </Modal>
  )
}
